import {
  Check,
  CheckCircle2,
  MessageCircleMore,
  PackageCheck,
  ReceiptText,
  Truck,
  X,
} from "lucide-react";
import { useEffect } from "react";
import { useAppSelector } from "../../../../Redux/hooks";
import { RootState } from "../../../../store";
import {
  formatFoodOrderLongDate,
  formatFoodCurrency,
  getFoodOrderItemCount,
  mapOrderToFoodOrderRecord,
  FoodOrderRecord,
} from "../ordersAdapters";
import "./FoodOrderTrackingModal.css";

type FoodOrderTrackingModalProps = {
  open: boolean;
  onClose: () => void;
  onChat: () => void;
};

const STAGE_INDEX: Record<FoodOrderRecord["stage"], number> = {
  placed: 0,
  prepared: 1,
  "out-for-delivery": 2,
  delivered: 3,
  cancelled: -1,
};

const FoodOrderTrackingModal = ({
  open,
  onClose,
  onChat,
}: FoodOrderTrackingModalProps) => {
  const { currentOrder, loading } = useAppSelector(
    (state: RootState) => state.customerOrders,
  );

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [open, onClose]);

  if (!open) return null;

  const order = currentOrder ? mapOrderToFoodOrderRecord(currentOrder) : null;
  const currentIndex = order ? STAGE_INDEX[order.stage] : -1;
  const delivered = order?.stage === "delivered";
  const cancelled = order?.stage === "cancelled";

  return (
    <div
      className="food-track-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section
        className="food-track-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="food-track-title"
      >
        {loading || !order ? (
          <div className="food-track-loading">
            {loading ? "Loading order details..." : "No order selected."}
            <button
              type="button"
              className="food-track-close"
              onClick={onClose}
              aria-label="Close tracking"
            >
              <X size={20} strokeWidth={2} />
            </button>
          </div>
        ) : (
          <>
            <header className="food-track-header">
              <div>
                <span className="food-track-kicker">LIVE ORDER STATUS</span>
                <h2 id="food-track-title">Order #{order.id}</h2>
                <p>
                  {getFoodOrderItemCount(order)} items ·{" "}
                  {formatFoodCurrency(order.bill.grandTotal)}
                </p>
              </div>
              <div className="food-track-header-actions">
                <span
                  className={`food-track-status status-${order.stage}`}
                >
                  {order.stageLabel}
                </span>
                <button
                  type="button"
                  className="food-track-close"
                  onClick={onClose}
                  aria-label="Close tracking"
                >
                  <X size={20} strokeWidth={2} />
                </button>
              </div>
            </header>

            {cancelled ? (
              <div className="food-track-cancelled">
                <span>
                  <X size={22} strokeWidth={2.2} />
                </span>
                <div>
                  <strong>Order Cancelled</strong>
                  <p>
                    Tracking is no longer available for this order.
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="food-track-timeline-shell">
                  <div className="food-track-timeline">
                    {order.tracking.map((step, index) => {
                      const isCompleted = index < currentIndex;
                      const isCurrent = index === currentIndex;
                      const isUpcoming = index > currentIndex;

                      return (
                        <div
                          className={`food-track-step ${
                            isCompleted ? "completed" : ""
                          } ${isCurrent ? "current" : ""} ${
                            isUpcoming ? "upcoming" : ""
                          }`}
                          key={step.id}
                        >
                          <div className="food-track-step-line">
                            <span className="food-track-step-node">
                              {isCompleted && (
                                <Check size={12} strokeWidth={3} />
                              )}
                              {isCurrent && (
                                <span className="food-track-pulse-dot" />
                              )}
                            </span>
                          </div>
                          <strong>{step.label}</strong>
                          <span className="food-track-step-time">
                            {step.time || "—"}
                          </span>
                          <small>
                            {isCompleted
                              ? "Completed"
                              : isCurrent
                                ? "Current stage"
                                : "Pending"}
                          </small>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="food-track-update-card">
                  <span className="food-track-update-icon">
                    {delivered ? (
                      <CheckCircle2 size={22} strokeWidth={2} />
                    ) : (
                      <Truck size={22} strokeWidth={2} />
                    )}
                  </span>
                  <div>
                    <span className="food-track-update-label">
                      LATEST UPDATE
                    </span>
                    <strong>{order.stageLabel}</strong>
                    <p>
                      {delivered && order.deliveredAt
                        ? `Delivered on ${formatFoodOrderLongDate(
                            order.deliveredAt,
                          )}${
                            order.deliveredTime
                              ? `, ${order.deliveredTime}`
                              : ""
                          }${order.riderName ? ` by ${order.riderName}` : ""}.`
                        : order.statusMessage}
                    </p>
                  </div>
                </div>
              </>
            )}

            <div className="food-track-content-grid">
              <section className="food-track-items-card">
                <div className="food-track-section-title">
                  <span>
                    <PackageCheck size={18} strokeWidth={2} />
                  </span>
                  <div>
                    <small>ORDER ITEMS</small>
                    <h3>{getFoodOrderItemCount(order)} items</h3>
                  </div>
                </div>
                <div className="food-track-items-list">
                  {order.items.map((item) => (
                    <div className="food-track-item-row" key={item.id}>
                      <span
                        className={`food-track-food-mark ${
                          item.isVeg ? "veg" : "non-veg"
                        }`}
                        aria-label={
                          item.isVeg ? "Vegetarian" : "Non vegetarian"
                        }
                      >
                        <i />
                      </span>
                      <span>
                        {item.name} <b>x {item.quantity}</b>
                      </span>
                      <strong>
                        {formatFoodCurrency(item.price * item.quantity)}
                      </strong>
                    </div>
                  ))}
                </div>
              </section>

              <section className="food-track-bill-card">
                <div className="food-track-section-title">
                  <span>
                    <ReceiptText size={18} strokeWidth={2} />
                  </span>
                  <div>
                    <small>BILL DETAILS</small>
                    <h3>Payment summary</h3>
                  </div>
                </div>
                <div className="food-track-bill-list">
                  <div>
                    <span>Item Total</span>
                    <strong>{formatFoodCurrency(order.bill.itemTotal)}</strong>
                  </div>
                  <div>
                    <span>Platform Fee</span>
                    <strong>
                      {formatFoodCurrency(order.bill.platformFee)}
                    </strong>
                  </div>
                  {order.bill.discountAmount > 0 && (
                    <div className="discount-row">
                      <span>
                        Discount
                        {order.bill.discountLabel
                          ? ` (${order.bill.discountLabel})`
                          : ""}
                      </span>
                      <strong>
                        -{formatFoodCurrency(order.bill.discountAmount)}
                      </strong>
                    </div>
                  )}
                  {Boolean(order.bill.shoppingVoucher) && (
                    <div>
                      <span>Salar Shopping Voucher</span>
                      <strong>
                        -{formatFoodCurrency(order.bill.shoppingVoucher ?? 0)}
                      </strong>
                    </div>
                  )}
                  {Boolean(order.bill.salarRuby) && (
                    <div>
                      <span>Salar Ruby</span>
                      <strong>
                        -{formatFoodCurrency(order.bill.salarRuby ?? 0)}
                      </strong>
                    </div>
                  )}
                  <div>
                    <span>Delivery Fee</span>
                    <strong>
                      {order.bill.deliveryFee === 0
                        ? "FREE"
                        : formatFoodCurrency(order.bill.deliveryFee)}
                    </strong>
                  </div>
                  <div>
                    <span>Taxes</span>
                    <strong>{formatFoodCurrency(order.bill.taxes)}</strong>
                  </div>
                  <div className="food-track-grand-total">
                    <span>BILL TOTAL</span>
                    <strong>
                      {formatFoodCurrency(order.bill.grandTotal)}
                    </strong>
                  </div>
                  <div>
                    <span>Paid via</span>
                    <strong>
                      {currentOrder?.paymentDetails?.onlinePaymentGateway ||
                        currentOrder?.paymentDetails?.paymentInstrument ||
                        "Cash"}
                    </strong>
                  </div>
                </div>
              </section>
            </div>

            <footer className="food-track-footer">
              <div>
                <span>Need help with this food order?</span>
                <strong>We&apos;re here to help.</strong>
              </div>
              <button type="button" onClick={onChat}>
                <MessageCircleMore size={18} strokeWidth={2} />
                Chat with us
              </button>
            </footer>
          </>
        )}
      </section>
    </div>
  );
};

export default FoodOrderTrackingModal;
